// AdvancedOptions — the "Advanced options" expander that every block editor pane
// uses to split everyday sub-controls (default-visible) from pro sub-controls.
//
// WL-011 (Fable Hail Mary Wave 2). Implements DESIGN-DECISIONS-LOG row 39:
//   "every block's pane splits everyday sub-controls (default-visible) from pro
//    sub-controls ('Advanced options' expander)".
//
// Conforms to the deck's canonical Role-A "E1 plain overline skin" strip
// (design-handoff/concepts/edit-wheel-flow/12-text-block-advanced-options.html):
//   10px/700 uppercase #8F38EF label + 1px #E9DBFC rule + caret; NO tinted box.
// Accessibility (WL-027): real <button>, aria-expanded, aria-controls, >=44px
// target, visible focus ring, keyboard-operable, reduced-motion honored.

import React, { useId, useState } from "react";
import styled from "styled-components";
import { fontFamily, brand, focusRing } from "../../../theme/tokens";

// Purple600 AA on-text token (decisions-log row 19). Label = darkened primary
// (#8F38EF, AA on white); focus = brand accent (#9B5AF8). Both from tokens.brand.
const ADV = {
  label: brand.primaryActive, // #8F38EF
  rule: "#E9DBFC",
  hover: "#FAF7FE",
  focus: brand.primary, // #9B5AF8
};

const Wrap = styled.div`
  margin: 6px 0 4px;
  box-sizing: border-box;
  & * { box-sizing: border-box; }
`;

const Summary = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  padding: 0 2px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: ${fontFamily};
  text-align: left;
  transition: background-color 0.2s ease;

  &:hover { background: ${ADV.hover}; }
  &:focus-visible {
    outline: none;
    box-shadow: ${focusRing.shadow};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Label = styled.span`
  flex: none;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${ADV.label};
  line-height: 1;
  font-family: ${fontFamily};
`;

const Rule = styled.span`
  flex: 1;
  height: 1px;
  background: ${ADV.rule};
`;

const Caret = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  padding-right: 2px;
  color: ${ADV.label};
  transition: transform 0.3s ease;
  transform: ${(p) => (p.$open ? "rotate(180deg)" : "none")};

  svg { width: 16px; height: 16px; }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.$dense ? "4px" : "6px")};
  padding-top: ${(p) => (p.$dense ? "4px" : "6px")};
`;

const ChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * @param {string}  label     strip label (default "Advanced options")
 * @param {boolean} defaultOpen  start expanded (default false)
 * @param {boolean} dense     tighter body spacing
 * @param {node}    children  the pro sub-controls
 */
const AdvancedOptions = ({
  label = "Advanced options",
  defaultOpen = false,
  dense = false,
  children,
  ...rest
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();

  return (
    <Wrap {...rest}>
      <Summary
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((o) => !o)}
      >
        <Label>{label}</Label>
        <Rule aria-hidden="true" />
        <Caret $open={open} aria-hidden="true">
          <ChevronDown />
        </Caret>
      </Summary>
      <Body id={bodyId} $dense={dense} hidden={!open}>
        {children}
      </Body>
    </Wrap>
  );
};

export default AdvancedOptions;
